import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import {
  ArrowLeft,
  Sparkles,
  Flame,
  Share2,
  Download,
  Copy,
  Loader2,
  MessageCircle,
  TrendingDown,
  Heart,
  Calendar,
  Target,
  PartyPopper,
  Instagram,
  CheckCircle2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import ReserveDealModal from "@/components/ReserveDealModal";
import { Events } from "@/lib/analytics";

const SHARE_URL = "https://sayitbetter.me/wrapped-preview";

const CAPTIONS = {
  yearly:
    "My Texting Wrapped™ is wild 😅 — SayItBetter rewrote the worst texts I almost sent this year. Get yours: " +
    SHARE_URL,
  monthly:
    "My June text recap 📉 — I sent fewer risky texts this month thanks to SayItBetter. Get yours: " +
    SHARE_URL,
};

// Fixed demo numbers — same for everyone so shares stay on-brand and believable.
const DEMO = {
  messagesRewritten: 847,
  avgRiskBefore: 6.8,
  avgRiskAfter: 2.4,
  biggestSaveFrom: 9.2,
  biggestSaveTo: 2.1,
  topTone: "Direct & kind",
  topTonePct: 68,
  topRelationship: "Romantic partner",
  hottestMonth: "November",
  hottestMonthCount: 142,
  closestCallQuote:
    "\"I honestly can't believe you'd do this again. You never listen and I'm DONE pretending everything is fine.\"",
  closestCallRisk: 9.4,
  closestCallFixed: 1.9,
};

// Monthly-view demo numbers — smaller, more coaching-focused stats.
const DEMO_MONTH = {
  month: "June",
  messagesRewritten: 64,
  calmDaysBefore: 9,
  calmDaysAfter: 22,
  topToneShort: "Direct & kind",
  streakDays: 7,
  topInsight: "Your calm days more than doubled vs. May.",
  nudge: "Nice work — keep using the scheduler for late-night drafts.",
};

function StatRow({ icon: Icon, label, value, accent = false }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
          accent ? "bg-white/20 border border-white/30" : "bg-white/10 border border-white/20"
        }`}
      >
        <Icon size={16} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70">
          {label}
        </div>
        <div className={`${accent ? "text-2xl" : "text-lg"} font-bold text-white leading-tight`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function WrappedCard({ forCapture = false }) {
  return (
    <div
      className={`relative w-full ${forCapture ? "" : "max-w-sm mx-auto"} rounded-[28px] overflow-hidden shadow-2xl`}
      style={{
        background:
          "linear-gradient(145deg, #0B1A33 0%, #1F3A5F 30%, #2F5BFF 70%, #4FB3A5 100%)",
        aspectRatio: forCapture ? "1080/1350" : undefined,
      }}
    >
      {/* Decorative blobs */}
      <div
        className="absolute -top-10 -right-10 w-56 h-56 rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, #FFD27F 0%, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-16 -left-10 w-64 h-64 rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, #4FB3A5 0%, transparent 70%)" }}
      />

      <div className="relative p-7 flex flex-col h-full min-h-[640px]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 text-white text-[10px] font-bold px-3 py-1 rounded-full">
            <Sparkles size={11} />
            SAYITBETTER
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-white/70">
            2026 · Demo
          </div>
        </div>

        {/* Hero stat */}
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-widest font-bold text-white/70 mb-2">
            Your Texting Wrapped™
          </div>
          <div className="text-5xl font-black text-white leading-none tracking-tight">
            {DEMO.messagesRewritten.toLocaleString()}
          </div>
          <div className="text-sm text-white/80 mt-1">
            texts rewritten before they detonated 💣
          </div>
        </div>

        {/* Trend ribbon */}
        <div className="bg-white/12 backdrop-blur-md border border-white/25 rounded-2xl p-4 mb-5">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-3xl font-black text-white">
              {DEMO.avgRiskBefore.toFixed(1)}
            </span>
            <TrendingDown size={18} className="text-[#FFD27F]" />
            <span className="text-3xl font-black text-[#FFD27F]">
              {DEMO.avgRiskAfter.toFixed(1)}
            </span>
            <span className="text-xs text-white/70 ml-auto">avg risk score</span>
          </div>
          <div className="text-[12px] text-white/80 leading-snug">
            You dropped your average message risk by{" "}
            <span className="font-bold text-white">
              {Math.round(
                ((DEMO.avgRiskBefore - DEMO.avgRiskAfter) / DEMO.avgRiskBefore) * 100
              )}
              %
            </span>{" "}
            this year.
          </div>
        </div>

        {/* Stat grid */}
        <div className="space-y-3 mb-5">
          <StatRow icon={Heart}       label="Top tone"          value={`${DEMO.topTone} · ${DEMO.topTonePct}%`} />
          <StatRow icon={MessageCircle} label="Most messaged"    value={DEMO.topRelationship} />
          <StatRow icon={Calendar}    label="Hottest month"     value={`${DEMO.hottestMonth} — ${DEMO.hottestMonthCount} rewrites`} />
          <StatRow icon={Target}      label="Biggest save"      value={`${DEMO.biggestSaveFrom} → ${DEMO.biggestSaveTo}`} accent />
        </div>

        {/* Closest call quote */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 mb-5">
          <div className="flex items-center gap-1.5 mb-2">
            <Flame size={12} className="text-[#FFD27F]" />
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#FFD27F]">
              Closest call
            </span>
          </div>
          <p className="text-[13px] text-white/90 italic leading-relaxed mb-2">
            {DEMO.closestCallQuote}
          </p>
          <div className="flex items-center gap-2 text-[11px] text-white/70">
            <span className="bg-[#E07A7A]/30 border border-[#E07A7A]/50 text-[#FFD6D6] font-bold px-2 py-0.5 rounded-full">
              Risk {DEMO.closestCallRisk.toFixed(1)}
            </span>
            <span>→</span>
            <span className="bg-[#4FB3A5]/30 border border-[#4FB3A5]/50 text-white font-bold px-2 py-0.5 rounded-full">
              Sent at {DEMO.closestCallFixed.toFixed(1)}
            </span>
            <span className="ml-auto font-semibold">Saved.</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 flex items-center justify-between">
          <div className="text-[11px] text-white/75 leading-tight">
            Get yours at launch
            <br />
            <span className="text-white font-bold">sayitbetter.me</span>
          </div>
          <div className="inline-flex items-center gap-1.5 bg-[#FFD27F] text-[#5E3B00] text-[11px] font-black px-3 py-1.5 rounded-full shadow-md">
            <Flame size={11} />
            $5.99/yr PRE-LAUNCH
          </div>
        </div>
      </div>
    </div>
  );
}

function MonthlyCard({ forCapture = false }) {
  const calmLift = Math.round(
    ((DEMO_MONTH.calmDaysAfter - DEMO_MONTH.calmDaysBefore) / DEMO_MONTH.calmDaysBefore) * 100
  );
  return (
    <div
      className={`relative w-full ${forCapture ? "" : "max-w-sm mx-auto"} rounded-[28px] overflow-hidden shadow-2xl bg-white`}
      style={{ aspectRatio: forCapture ? "1/1" : undefined }}
    >
      {/* Hero ribbon */}
      <div
        className="relative px-6 pt-6 pb-5 text-white"
        style={{
          background: "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 50%, #4FB3A5 100%)",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 text-white text-[10px] font-bold px-3 py-1 rounded-full">
            <Sparkles size={11} />
            SAYITBETTER
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-white/70">
            Monthly recap
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-widest font-bold text-white/70 mb-1">
          Your {DEMO_MONTH.month} recap
        </div>
        <div className="flex items-baseline gap-3">
          <span className="text-5xl font-black leading-none tracking-tight">
            {DEMO_MONTH.messagesRewritten}
          </span>
          <span className="text-sm text-white/85">texts rewritten</span>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5">
        <div className="bg-[#EAF6F4] border border-[#4FB3A5]/30 rounded-2xl p-4 mb-4">
          <div className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-[#4FB3A5] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[13px] font-bold text-[#1F3A5F] leading-snug">
                {DEMO_MONTH.topInsight}
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-2xl font-black text-[#1F3A5F]">
                  {DEMO_MONTH.calmDaysAfter}
                </span>
                <span className="text-[11px] text-[#6B7280]">calm days</span>
                <span className="ml-auto text-[11px] font-bold text-[#4FB3A5]">
                  +{calmLift}% vs. May
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-[#F7F9FB] border border-[#E5E7EB] rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Heart size={12} className="text-[#E07A7A]" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#A0A7B4]">
                Top tone
              </span>
            </div>
            <div className="text-[13px] font-bold text-[#1F3A5F] leading-tight">
              {DEMO_MONTH.topToneShort}
            </div>
          </div>
          <div className="bg-[#F7F9FB] border border-[#E5E7EB] rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Flame size={12} className="text-[#FF9A4D]" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#A0A7B4]">
                Streak
              </span>
            </div>
            <div className="text-[13px] font-bold text-[#1F3A5F] leading-tight">
              {DEMO_MONTH.streakDays} days safe-sending
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2 bg-[#1F3A5F]/5 rounded-xl p-3 mb-3">
          <Zap size={14} className="text-[#2F5BFF] flex-shrink-0 mt-0.5" />
          <p className="text-[12px] text-[#1F3A5F] leading-snug">
            <span className="font-semibold">Coach tip:</span> {DEMO_MONTH.nudge}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-[10px] text-[#6B7280]">
            See more at <span className="font-bold text-[#1F3A5F]">sayitbetter.me</span>
          </div>
          <div className="inline-flex items-center gap-1 bg-[#4FB3A5] text-white text-[10px] font-black px-2.5 py-1 rounded-full">
            <Flame size={10} />
            $5.99/yr PRO
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WrappedPreviewPage() {
  const cardRef = useRef(null);
  const monthlyRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [view, setView] = useState("monthly"); // "monthly" | "yearly"

  useEffect(() => {
    Events.trackEvent?.("wrapped_preview_view", { source: "public" });
  }, []);

  useEffect(() => {
    Events.trackEvent?.("wrapped_view_toggle", { view });
  }, [view]);

  const capture = async () => {
    const el = view === "yearly" ? cardRef.current : monthlyRef.current;
    if (!el) return null;
    el.style.position = "fixed";
    el.style.left = "0";
    el.style.top = "0";
    el.style.zIndex = "-1";
    el.style.opacity = "1";
    try {
      const dataUrl = await toPng(el, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: view === "yearly" ? "#0B1A33" : "#FFFFFF",
      });
      return dataUrl;
    } finally {
      el.style.position = "absolute";
      el.style.left = "-99999px";
      el.style.top = "-99999px";
      el.style.opacity = "0";
    }
  };

  const dataUrlToFile = async (dataUrl) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const name = view === "yearly" ? "texting-wrapped.png" : "texting-recap-june.png";
    return new File([blob], name, { type: "image/png" });
  };

  const shareCaption = CAPTIONS[view];

  const handleShareImage = async () => {
    setBusy(true);
    Events.trackEvent?.("wrapped_share_click", { view });
    try {
      const dataUrl = await capture();
      if (!dataUrl) throw new Error("capture failed");
      const file = await dataUrlToFile(dataUrl);
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          files: [file],
          title: view === "yearly" ? "My Texting Wrapped" : "My Texting Recap",
          text: shareCaption,
        });
        Events.shareApp?.(`wrapped_native_${view}`);
        toast.success("Shared!");
        return;
      }
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = view === "yearly" ? "texting-wrapped.png" : "texting-recap-june.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      try {
        await navigator.clipboard.writeText(shareCaption);
      } catch { /* noop */ }
      Events.shareApp?.(`wrapped_download_${view}`);
      toast.success("Image saved — caption copied too. Post it anywhere!");
    } catch {
      toast.error("Couldn't build the image. Try copy-link instead.");
    } finally {
      setBusy(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      Events.shareApp?.(`wrapped_copy_link_${view}`);
      toast.success("Link copied!");
    } catch {
      toast.error("Couldn't copy — long-press the URL bar instead.");
    }
  };

  const handleTweet = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareCaption)}`;
    Events.shareApp?.(`wrapped_twitter_${view}`);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F7F9FB] to-[#EAF6F4]" data-testid="wrapped-preview-page">
      {/* Hidden capture nodes */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-99999px",
          top: "-99999px",
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        {/* Yearly — 1080x1350 IG-portrait */}
        <div ref={cardRef} style={{ width: "1080px", padding: "0" }}>
          <WrappedCard forCapture />
        </div>
        {/* Monthly — 1080x1080 IG-square */}
        <div ref={monthlyRef} style={{ width: "1080px", padding: "0", marginTop: "40px" }}>
          <MonthlyCard forCapture />
        </div>
      </div>

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" data-testid="wrapped-back" className="text-[#6B7280] hover:text-[#1F3A5F] inline-flex items-center gap-2 text-sm font-medium">
            <ArrowLeft size={18} />
            <span>Home</span>
          </Link>
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#4FB3A5]">
            Texting Wrapped™
          </div>
          <div className="w-14" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6 pb-16">
        {/* Lead */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-1.5 bg-[#1F3A5F] text-white text-[11px] font-bold px-3 py-1.5 rounded-full mb-3">
            <Sparkles size={12} />
            DEMO — YOURS SHIPS AT LAUNCH
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-[#1F3A5F] leading-tight tracking-tight mb-2">
            {view === "yearly"
              ? "What your texts looked like this year."
              : "Your monthly text recap."}
          </h1>
          <p className="text-[14px] text-[#6B7280] max-w-md mx-auto leading-relaxed">
            {view === "yearly" ? (
              <>
                The viral year-end share card.{" "}
                <span className="font-semibold text-[#1F3A5F]">One big moment</span> every December
                that your friends will actually screenshot.
              </>
            ) : (
              <>
                The in-app coaching recap.{" "}
                <span className="font-semibold text-[#1F3A5F]">Every first of the month</span> —
                see your trend, your streak, and one smart nudge.
              </>
            )}
          </p>
        </div>

        {/* View toggle */}
        <div
          className="flex items-center justify-center mb-5"
          data-testid="wrapped-view-toggle"
        >
          <div className="inline-flex bg-white border border-[#E5E7EB] rounded-full p-1 shadow-sm">
            <button
              onClick={() => setView("monthly")}
              data-testid="wrapped-toggle-monthly"
              className={`px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                view === "monthly"
                  ? "bg-[#1F3A5F] text-white shadow-sm"
                  : "text-[#6B7280] hover:text-[#1F3A5F]"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setView("yearly")}
              data-testid="wrapped-toggle-yearly"
              className={`px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                view === "yearly"
                  ? "bg-[#1F3A5F] text-white shadow-sm"
                  : "text-[#6B7280] hover:text-[#1F3A5F]"
              }`}
            >
              Yearly Wrapped™
            </button>
          </div>
        </div>

        {/* Visible card */}
        <div className="mb-6">
          {view === "yearly" ? <WrappedCard /> : <MonthlyCard />}
        </div>

        {/* Share row */}
        <div className="grid grid-cols-3 gap-2 mb-5" data-testid="wrapped-share-row">
          <button
            onClick={handleShareImage}
            disabled={busy}
            data-testid="wrapped-share-image"
            className="bg-[#1F3A5F] hover:bg-[#2a4a75] text-white rounded-2xl py-3 flex flex-col items-center gap-1 font-semibold text-[12px] active:scale-95 transition-all disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            <span>Save image</span>
          </button>
          <button
            onClick={handleTweet}
            data-testid="wrapped-share-twitter"
            className="bg-black text-white rounded-2xl py-3 flex flex-col items-center gap-1 font-semibold text-[12px] active:scale-95 transition-all"
          >
            <Share2 size={16} />
            <span>Post on X</span>
          </button>
          <button
            onClick={handleCopyLink}
            data-testid="wrapped-copy-link"
            className="bg-white text-[#1F3A5F] border border-[#E5E7EB] rounded-2xl py-3 flex flex-col items-center gap-1 font-semibold text-[12px] active:scale-95 transition-all"
          >
            <Copy size={16} />
            <span>Copy link</span>
          </button>
        </div>

        <p className="text-[11px] text-[#A0A7B4] text-center mb-8 leading-snug">
          <Instagram size={11} className="inline -mt-0.5" /> Works on IG Stories, TikTok, and group chats —
          {view === "yearly" ? " the 1080×1350 image" : " the 1080×1080 square"} downloads in one tap.
        </p>

        {/* CTA — Reserve */}
        <div
          className="rounded-3xl p-6 text-white relative overflow-hidden shadow-xl"
          style={{
            background: "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 40%, #4FB3A5 100%)",
          }}
        >
          <div
            className="absolute inset-0 opacity-25"
            style={{
              background: "radial-gradient(circle at 85% 20%, rgba(255,255,255,0.6), transparent 45%)",
            }}
          />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 text-white text-[10px] font-bold px-3 py-1 rounded-full mb-3">
              <PartyPopper size={11} />
              WANT YOUR OWN?
            </div>
            <h2 className="text-2xl font-black leading-tight mb-1">
              Reserve PRO for $5.99/year.
            </h2>
            <p className="text-white/85 text-sm leading-relaxed mb-4">
              Monthly recaps + your big year-end Wrapped — both ship on launch day. Pre-launchers lock in $5.99/yr for life. No charge today.
            </p>
            <button
              onClick={() => {
                Events.upgradeClick?.("yearly_5_99", "wrapped_preview");
                setReserveOpen(true);
              }}
              data-testid="wrapped-reserve-btn"
              className="w-full bg-white text-[#1F3A5F] font-black py-3.5 rounded-xl hover:bg-white/90 active:scale-95 transition-all shadow-lg text-[15px]"
            >
              Reserve my spot
            </button>
            <p className="text-[11px] text-white/70 mt-3 text-center">
              No payment today · Cancel anytime in-app · Private and encrypted
            </p>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-[11px] text-[#A0A7B4] text-center mt-6 leading-snug">
          Numbers shown are a demo. Real Wrapped will use your own private message history.
        </p>
      </div>

      <ReserveDealModal
        isOpen={reserveOpen}
        onClose={() => setReserveOpen(false)}
        source="wrapped_preview"
      />
    </div>
  );
}
